import { useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, ClipboardCheck, RefreshCw } from "lucide-react";
import type { Reading } from "@/lib/api";
import { celsiusToFahrenheit, EXTREMES, fitsLimit, INPUT_LIMITS } from "@/lib/vitalLimits";
import {
  flagFor, flagTone, flagValue, fmtNum, fmtTime, latestRecordedAt, patientAgeMonths as ageMonthsOf,
  recordedByLine, shortDate, todayToPrescriptionVitals,
} from "@/lib/vitals";
import { useTodayVitalsContext, useTodayVitalsSource } from "@/context/TodayVitalsContext";
import {
  VITAL_RANGES,
  PAEDIATRIC_VITAL_RANGES,
  getPaediatricAgeGroup,
} from "@/lib/constants";
import SectionPropertiesProps from "@/components/shared/SectionProperties.tsx";

interface Range { min: number; max: number; normal: string; }

interface VitalWarning {
  show: boolean;
}

/**
 * Only flags values that are physically impossible / clear data entry errors.
 * Triggered only on blur — not while typing.
 */
function getVitalWarning(value: string, extremes: { min: number; max: number }): VitalWarning {
  if (!value || value === "") return { show: false };
  const num = parseFloat(value);
  if (isNaN(num)) return { show: false };
  if (num < extremes.min || num > extremes.max) return { show: true };
  return { show: false };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

// As in the mockup: value and unit in one 36px box, the label and previous value as a caption under it

/** The box around a vital's input(s) and its unit */
function VitalBox({ unit, warn, children }: { unit: string; warn?: boolean; children: React.ReactNode }) {
  return (
      <span className={cn(
          "inline-flex items-stretch h-9 touch:h-10 rounded-md border bg-card overflow-hidden transition-colors",
          "focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/20",
          warn ? "border-destructive/60" : "border-field",
      )}>
        {children}
        <span className="flex items-center px-2 bg-input border-l border-border/70 text-xs text-muted-foreground">{unit}</span>
      </span>
  );
}

const VALUE_INPUT = cn(
    "h-full bg-transparent border-0 outline-none px-2.5 font-mono text-sm font-semibold text-foreground",
    "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
);

// No message — just a red border on the box, shown only after blur

function InputWithUnit({
                         unit, value, onChange, title, extremes, flag, code, onBlur, wholeNumber,
                       }: {
  unit: string;
  value: string;
  onChange: (value: string) => void;
  title?: string;
  extremes?: { min: number; max: number };
  flag?: "H" | "L" | null;
  /** Reading code, so a low SpO2 shows red */
  code?: string;
  onBlur?: () => void;
  /** Pulse and SpO2 are whole numbers: the "." key does nothing */
  wholeNumber?: boolean;
}) {
  const [hasBlurred, setHasBlurred] = useState(false);
  const warning = hasBlurred && extremes ? getVitalWarning(value, extremes) : { show: false };

  return (
      <VitalBox unit={unit} warn={warning.show}>
        <input
            type="number"
            min="0"
            step="any"
            value={value}
            aria-label={title}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "" || (!isNaN(parseFloat(v)) && parseFloat(v) >= 0)) onChange(v);
            }}
            onBlur={() => { setHasBlurred(true); onBlur?.(); }}
            onKeyDown={(e) => {
              if (e.key === "-" || e.key === "e" || e.key === "E" || (wholeNumber && e.key === ".")) e.preventDefault();
            }}
            title={title}
            className={cn(VALUE_INPUT, "w-16", flagText(flag, code))}
        />
      </VitalBox>
  );
}

const flagText = (flag?: "H" | "L" | null, code?: string) =>
    flag ? `${flagTone(flag, code)} font-semibold` : "";

/** "BP · prev 120/80" under the box, with the H/L badge for the value in it; the previous reading's date on hover */
function VitalCaption({ label, flag, code, prev }: { label: string; flag?: "H" | "L" | null; code?: string; prev?: { text: string; date: string } | null }) {
  return (
      <span className="mt-1 flex items-center gap-1 text-xs text-muted-foreground tabular-nums" title={prev ? `Previous ${prev.text}, ${prev.date}` : undefined}>
        {label}
        {flag && (
            <span className={cn("rounded px-1 text-2xs font-bold leading-4",
                flagTone(flag, code) === "text-destructive" ? "bg-destructive/10 text-destructive" : "bg-warning/15 text-warning")}
                  title={flag === "H" ? "Above the normal range for this age" : "Below the normal range for this age"}>
              {flag}
            </span>
        )}
        {prev && <span>· prev {prev.text}</span>}
      </span>
  );
}

const prevOf = (r?: Reading, value?: string) => (r ? { text: value ?? fmtNum(r.value), date: shortDate(r.effectiveAt) } : null);

// ─── Component ────────────────────────────────────────────────────────────────

const PRESCRIPTION_VITAL_KEYS = ["bp", "pulse", "temp", "spo2", "weight"] as const;

/** The most recently recorded of today's readings, to credit who took the latest set */
const newestReading = (t: Partial<Record<string, Reading>>) => {
  const at = latestRecordedAt(t as any);
  return Object.values(t).find(r => r?.recordedAt === at);
};

/** Section id — the header's vitals strip scrolls here when a newer set is waiting */
export const RX_VITALS_SECTION_ID = "rx-vitals-section";

export default function VitalsForm({ patientAgeMonths, patient, prefillTodayVitals = false }: SectionPropertiesProps) {
  const { setValue, getValues, watch, reset, formState: { errors, isDirty } } = useFormContext();

  const vitals = watch("vitals") || {};

  // ── Today's recorded vitals ─────────────────────────────────────────────────
  // Today's vitals recorded at triage: filled into a new prescription when its vitals are empty, and
  // offered (never forced) when a newer set is recorded while the prescription is open. The patient
  // page shares one source with its header strip; elsewhere this section loads its own.
  const shared = useTodayVitalsContext();
  const own = useTodayVitalsSource(patient?.id, prefillTodayVitals && !shared);
  const today = prefillTodayVitals ? (shared ? shared.data : own.data) : null;
  const newest = latestRecordedAt(today?.today);
  // Recorded time of the set already in the form (filled in or dismissed), so only newer sets are offered
  const [ownAppliedAt, setOwnAppliedAt] = useState<string | null>(null);
  const appliedAt = shared ? shared.appliedAt : ownAppliedAt;
  const setAppliedAt = shared ? shared.setAppliedAt : setOwnAppliedAt;
  // A new form starts with nothing applied
  useEffect(() => { shared?.setAppliedAt(null); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [filledFrom, setFilledFrom] = useState<string | null>(null);
  // Set once the first load has been handled (filled in, or left for the doctor to accept)
  const [checked, setChecked] = useState(false);

  const applyToday = useCallback((markDirty: boolean) => {
    if (!today) return;
    // Taking a newer set applies only what was re-measured since the set already in the form
    const next = todayToPrescriptionVitals(today.today, markDirty ? appliedAt : null);
    if (!markDirty && !isDirty) {
      // Silent fill into an untouched form: make the values part of its starting point. Plain setValue
      // would leave them differing from the empty defaults, and the next change anywhere (even the
      // medicines table adding its first row) would count them as unsaved edits.
      const current = getValues();
      reset({ ...current, vitals: { ...(current.vitals || {}), ...next } }, { keepErrors: true });
    } else {
      for (const key of PRESCRIPTION_VITAL_KEYS) {
        if (next[key] !== undefined) setValue(`vitals.${key}`, next[key], { shouldDirty: true });
      }
    }
    setFilledFrom(recordedByLine(newestReading(today.today)));
    setAppliedAt(latestRecordedAt(today.today));
  }, [today, setValue, setAppliedAt, isDirty, getValues, reset, appliedAt]);

  useEffect(() => {
    if (!today || !newest || checked) return;
    setChecked(true);
    const current = getValues("vitals") || {};
    const empty = PRESCRIPTION_VITAL_KEYS.every(k => !current[k]);
    const next = todayToPrescriptionVitals(today.today);
    const alreadyToday = !empty && PRESCRIPTION_VITAL_KEYS.every(k => (current[k] || undefined) === next[k]);
    // Fill silently only into an empty section; a restored draft keeps its values and gets the offer
    // instead. Fields already holding today's set (the section was re-created) count as applied.
    if (empty) applyToday(false);
    else if (alreadyToday) {
      setAppliedAt(newest);
      setFilledFrom(recordedByLine(newestReading(today.today)));
    }
  }, [today, newest, checked, getValues, applyToday, setAppliedAt]);

  const offerNewer = checked && !!newest && newest !== appliedAt;
  const alsoRecorded = today ? (["height", "rbs"] as const).filter(c => today.today[c]) : [];
  // Flags follow what is in the field now, with paediatric ranges under 18
  const ageMonths = patientAgeMonths ?? ageMonthsOf(patient);
  const types = today?.types;
  const prev = today?.previous ?? {};
  const num = (v: unknown) => (v === "" || v == null ? null : parseFloat(String(v)));
  const [bpSys, setBpSys] = useState("");
  const [bpDia, setBpDia] = useState("");
  const bpDiaRef = useRef<HTMLInputElement>(null);
  // Moving on to diastolic selects what's there, so typing replaces it
  const toDiastolic = () => { bpDiaRef.current?.focus(); bpDiaRef.current?.select(); };
  // Track which fields have been blurred — only show highlight after user leaves field
  const [blurred, setBlurred] = useState<Set<string>>(new Set());
  const markBlurred = (field: string) => setBlurred((prev) => new Set(prev).add(field));

  useEffect(() => {
    const currentBP = vitals.bp || "";
    const expectedBP = bpSys && bpDia ? `${bpSys}/${bpDia}`
        : bpSys ? bpSys : bpDia ? `/${bpDia}` : "";
    if (currentBP !== expectedBP) {
      if (currentBP.includes("/")) {
        const [sys, dia] = currentBP.split("/");
        setBpSys(sys || ""); setBpDia(dia || "");
      } else if (!bpSys && !bpDia && currentBP) {
        setBpSys(currentBP); setBpDia("");
      } else if (!currentBP) {
        setBpSys(""); setBpDia("");
      }
    }
  }, [vitals.bp]);

  const updateBP = (sys: string, dia: string) => {
    const combined = sys && dia ? `${sys}/${dia}` : sys ? sys : dia ? `/${dia}` : "";
    setValue("vitals.bp", combined, { shouldDirty: true });
  };

  const handleBpSysChange = (value: string) => {
    if (fitsLimit(value, INPUT_LIMITS.BP_SYS)) {
      setBpSys(value); updateBP(value, bpDia);
      // Systolic is at most 3 digits: the third one moves on to diastolic, so "13085" can't happen
      if (value.length === 3 && bpSys.length === 2) toDiastolic();
    }
  };

  const handleBpDiaChange = (value: string) => {
    if (fitsLimit(value, INPUT_LIMITS.BP_DIA)) {
      setBpDia(value); updateBP(bpSys, value);
    }
  };

  const handleValueChange = (field: keyof typeof INPUT_LIMITS, value: string) => {
    if (fitsLimit(value, INPUT_LIMITS[field])) setValue(`vitals.${field}`, value, { shouldDirty: true });
    if (field === "temp") setTempCelsius(null);
  };

  // A temperature typed in °C (38.5) becomes °F when the doctor leaves the box; the caption keeps the °C
  const [tempCelsius, setTempCelsius] = useState<string | null>(null);
  const convertTemp = () => {
    const f = celsiusToFahrenheit(vitals.temp || "");
    if (!f) return;
    setTempCelsius(vitals.temp);
    setValue("vitals.temp", f, { shouldDirty: true });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // BP is whole numbers
    if (e.key === "-" || e.key === "e" || e.key === "E" || e.key === ".") e.preventDefault();
  };

  // ── Range resolution ────────────────────────────────────────────────────────

  const getRange = (key: keyof typeof VITAL_RANGES): Range => {
    if (patientAgeMonths !== undefined && patientAgeMonths < 216) {
      const group = getPaediatricAgeGroup(patientAgeMonths);
      const paedRanges = PAEDIATRIC_VITAL_RANGES[group];
      if (key in paedRanges) return (paedRanges as any)[key];
    }
    return VITAL_RANGES[key];
  };

  // BP warnings — only after blur, only impossible values
  const bpSysWarning: VitalWarning = blurred.has("bpSys")
      ? getVitalWarning(bpSys, EXTREMES.BP_SYS)
      : { show: false };
  const bpDiaWarning: VitalWarning = blurred.has("bpDia")
      ? getVitalWarning(bpDia, EXTREMES.BP_DIA)
      : { show: false };
  const bpWarning: VitalWarning = { show: bpSysWarning.show || bpDiaWarning.show };

  // ── Render ──────────────────────────────────────────────────────────────────

  const bpFlag = flagValue("bp_systolic", num(bpSys), ageMonths, types) ?? flagValue("bp_diastolic", num(bpDia), ageMonths, types);
  const prevBp = prev.bp_systolic && prev.bp_diastolic
      ? prevOf(prev.bp_systolic, `${fmtNum(prev.bp_systolic.value)}/${fmtNum(prev.bp_diastolic.value)}`)
      : null;

  return (
      <div className="space-y-3 scroll-mt-40" id={RX_VITALS_SECTION_ID}>

        {/* Where the values came from, or a newer set to use */}
        {offerNewer ? (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1.5 text-xs">
              <RefreshCw className="w-3.5 h-3.5 text-primary shrink-0" />
              <span className="text-foreground">
                {appliedAt ? "New vitals recorded" : "Vitals recorded today"} by {recordedByLine(Object.values(today!.today).find(r => r!.recordedAt === newest))}
              </span>
              <button type="button" onClick={() => applyToday(true)} className="ml-auto font-semibold text-primary hover:underline">Use them</button>
              <button type="button" onClick={() => setAppliedAt(newest)} className="text-muted-foreground hover:underline">Keep mine</button>
            </div>
        ) : filledFrom && (
            <p className="flex items-center gap-1.5 text-2xs text-muted-foreground">
              <ClipboardCheck className="w-3.5 h-3.5 text-success shrink-0" />
              From today's vitals · {filledFrom}
            </p>
        )}

        {/* Main vitals — one box each, caption underneath, 16px apart */}
        <div className="flex flex-wrap items-start gap-4">

          {/* Blood pressure: systolic / diastolic in one box */}
          <label className="flex flex-col shrink-0">
            <VitalBox unit="mmHg" warn={bpWarning.show}>
              <input
                  type="number" min="0"
                  value={bpSys}
                  onChange={(e) => handleBpSysChange(e.target.value)}
                  onKeyDown={(e) => {
                    // "130/85" typed the usual way: the slash (or a space) moves to diastolic
                    if (e.key === "/" || e.key === " ") { e.preventDefault(); toDiastolic(); }
                    else handleKeyDown(e);
                  }}
                  className={cn(VALUE_INPUT, "w-14 pr-0 text-right", flagText(flagValue("bp_systolic", num(bpSys), ageMonths, types)))}
                  title="Systolic"
                  aria-label="Systolic"
                  onBlur={() => markBlurred("bpSys")}
              />
              <span className="flex items-center px-0.5 text-sm text-muted-foreground" aria-hidden>/</span>
              <input
                  ref={bpDiaRef}
                  type="number" min="0"
                  value={bpDia}
                  onChange={(e) => handleBpDiaChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className={cn(VALUE_INPUT, "w-12 pl-0", flagText(flagValue("bp_diastolic", num(bpDia), ageMonths, types)))}
                  title="Diastolic"
                  aria-label="Diastolic"
                  onBlur={() => markBlurred("bpDia")}
              />
            </VitalBox>
            <VitalCaption label="BP" flag={bpFlag} prev={prevBp} />
          </label>

          <label className="flex flex-col shrink-0">
            <InputWithUnit
                unit="bpm" title="Pulse"
                value={vitals.pulse || ""}
                onChange={(v) => handleValueChange("pulse", v)}
                wholeNumber
                extremes={EXTREMES.pulse}
                flag={flagValue("pulse", num(vitals.pulse), ageMonths, types)}
            />
            <VitalCaption label="Pulse" flag={flagValue("pulse", num(vitals.pulse), ageMonths, types)} prev={prevOf(prev.pulse)} />
          </label>

          <label className="flex flex-col shrink-0">
            <InputWithUnit
                unit="%" title="SpO2"
                value={vitals.spo2 || ""}
                onChange={(v) => handleValueChange("spo2", v)}
                wholeNumber
                extremes={EXTREMES.spo2}
                code="spo2"
                flag={flagValue("spo2", num(vitals.spo2), ageMonths, types)}
            />
            <VitalCaption label="SpO₂" code="spo2" flag={flagValue("spo2", num(vitals.spo2), ageMonths, types)} prev={prevOf(prev.spo2)} />
          </label>

          <label className="flex flex-col shrink-0">
            <InputWithUnit
                unit="°F" title="Temperature"
                value={vitals.temp || ""}
                onChange={(v) => handleValueChange("temp", v)}
                onBlur={convertTemp}
                extremes={EXTREMES.temp}
                flag={flagValue("temp", num(vitals.temp), ageMonths, types)}
            />
            <VitalCaption label={tempCelsius ? `Temp · ${tempCelsius} °C` : "Temp"} flag={flagValue("temp", num(vitals.temp), ageMonths, types)} prev={prevOf(prev.temp)} />
          </label>

          {/* Weight — no range flag, patient-specific */}
          <label className="flex flex-col shrink-0">
            <InputWithUnit
                unit="kg" title="Weight"
                value={vitals.weight || ""}
                onChange={(v) => handleValueChange("weight", v)}
                extremes={EXTREMES.weight}
            />
            <VitalCaption label="Weight" prev={prevOf(prev.weight)} />
          </label>
        </div>

        {/* Readings the prescription doesn't hold */}
        {alsoRecorded.length > 0 && (
            <p className="text-2xs text-muted-foreground">
              Also recorded today:{" "}
              {alsoRecorded.map(c => {
                const r = today!.today[c]!;
                const f = flagFor(r, ageMonths);
                return `${c === "rbs" ? "RBS" : "Height"} ${fmtNum(r.value)} ${r.unit}${f ? ` (${f})` : ""} at ${fmtTime(r.effectiveAt)}`;
              }).join(" · ")}
            </p>
        )}

        {errors.vitals && (
            <div className="text-xs text-destructive flex items-center gap-1.5 p-2 bg-destructive/5 border border-destructive/20 rounded">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>Please check vital signs</span>
            </div>
        )}
      </div>
  );
}